import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import { cameraFeedRecipe } from '../recipes/mobile.recipe';

/**
 * Where the camera is: opening, running, or why it cannot. The feed says
 * which; the screen around it decides what to show for each.
 *
 * - `denied`: the person, or the browser's settings, said no.
 * - `missing`: there is no camera, or nothing that can be asked for one.
 * - `insecure`: the page is not on HTTPS (or localhost), where a browser
 *   will not open a camera at all.
 * - `failed`: something else, usually another app holding the camera.
 */
export type CameraFeedState =
  | 'starting'
  | 'live'
  | 'denied'
  | 'missing'
  | 'insecure'
  | 'failed';

/**
 * Whether this page could open a camera: a secure context with
 * getUserMedia. It says nothing about whether the person will allow it;
 * only asking finds that out.
 */
export function canOpenCamera(): boolean {
  if (typeof window === 'undefined' || !window.isSecureContext) return false;
  return typeof navigator.mediaDevices?.getUserMedia === 'function';
}

/**
 * What the feed shows right now, as a JPEG file: the frame a shutter takes.
 * Null before the camera has drawn anything.
 */
export function captureFrame(
  video: HTMLVideoElement,
  name = 'camera.jpg'
): Promise<File | null> {
  const { videoWidth: width, videoHeight: height } = video;
  if (!width || !height) return Promise.resolve(null);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d')?.drawImage(video, 0, 0, width, height);
  return new Promise((resolve) =>
    canvas.toBlob(
      (blob) =>
        resolve(blob ? new File([blob], name, { type: 'image/jpeg' }) : null),
      'image/jpeg',
      0.9
    )
  );
}

const stateOf = (error: unknown): CameraFeedState => {
  const name = error instanceof Error ? error.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'denied';
  if (name === 'NotFoundError' || name === 'OverconstrainedError')
    return 'missing';
  return 'failed';
};

const useHidden = () => {
  const [hidden, setHidden] = React.useState(
    () => typeof document !== 'undefined' && document.hidden
  );
  React.useEffect(() => {
    const update = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);
  return hidden;
};

export interface CameraFeedProps
  extends Omit<
    React.VideoHTMLAttributes<HTMLVideoElement>,
    'children' | 'src' | 'autoPlay' | 'muted' | 'playsInline'
  > {
  /** Which camera: the rear one by default, the one that faces a vehicle. */
  facing?: 'environment' | 'user';
  /** Let go of the camera without unmounting, while a picture is read. */
  paused?: boolean;
  /** Told each time the camera opens, runs or cannot. */
  onStateChange?: (state: CameraFeedState) => void;
  /**
   * In a sheet, at a card's corners and taller than wide, rather than
   * filling a ScanFrame: the Enter VIN sheet's camera.
   */
  framed?: boolean;
}

/**
 * The phone's camera, live, as a <video> filling whatever holds it; the
 * feed a ScanFrame frames. It asks for the camera when it mounts and lets
 * go of it (every track stopped, so the phone's camera light goes out) when
 * it unmounts, when it is paused, and while the page is hidden, reopening
 * it when the page comes back. The ref is the video, for a caller that
 * reads frames off it.
 *
 *   <CameraFeed ref={videoRef} onStateChange={setCamera} />
 *
 * It draws nothing for the states where there is no picture; the caller
 * shows an EmptyState or Alert saying why, in the ScanFrame's notice.
 */
export const CameraFeed = React.forwardRef<HTMLVideoElement, CameraFeedProps>(
  function CameraFeed(
    {
      facing = 'environment',
      paused = false,
      onStateChange,
      framed = false,
      className,
      ...props
    },
    forwardedRef
  ) {
    const video = React.useRef<HTMLVideoElement | null>(null);
    const report = React.useRef(onStateChange);
    report.current = onStateChange;
    const hidden = useHidden();

    const setRef = React.useCallback(
      (node: HTMLVideoElement | null) => {
        video.current = node;
        if (typeof forwardedRef === 'function') forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
      },
      [forwardedRef]
    );

    React.useEffect(() => {
      if (paused || hidden) return;
      if (!canOpenCamera()) {
        report.current?.(window.isSecureContext ? 'missing' : 'insecure');
        return;
      }

      const element = video.current;
      let stream: MediaStream | null = null;
      let stopped = false;
      report.current?.('starting');
      navigator.mediaDevices
        .getUserMedia({
          video: { facingMode: { ideal: facing } },
          audio: false,
        })
        .then((opened) => {
          stream = opened;
          // Unmounted, paused or hidden while the person was answering the
          // prompt: let go straight away, or the camera stays on with
          // nothing showing it.
          if (stopped) {
            opened.getTracks().forEach((track) => track.stop());
            return;
          }
          if (element) {
            element.srcObject = opened;
            // iOS will not start a feed it was not told to play, autoPlay
            // or not; a refusal here is a page that lost focus, not an error.
            void element.play?.()?.catch(() => {});
          }
          report.current?.('live');
        })
        .catch((error: unknown) => {
          if (!stopped) report.current?.(stateOf(error));
        });

      return () => {
        stopped = true;
        stream?.getTracks().forEach((track) => track.stop());
        if (element) element.srcObject = null;
      };
    }, [facing, paused, hidden]);

    return (
      <video
        ref={setRef}
        autoPlay
        muted
        playsInline
        className={cn(cameraFeedRecipe({ framed }), className)}
        {...props}
      />
    );
  }
);
